import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CheckCircle, XCircle, Mail, ArrowLeft } from 'lucide-react';
import Button from '../../components/ui/Button';
import { verificationService } from '../../services/verificationService';
import { useAuth } from '../../contexts/AuthContext';


const EmailVerificationScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  
  // Get email, userId, and verificationId from navigation state
  const email = location?.state?.email || user?.email || '';
  const stateUserId = location?.state?.userId || user?.id || null;
  const initialVerificationId = location?.state?.verificationId || null;
  const phoneNumber = location?.state?.phoneNumber || '';
  
  const [verificationId, setVerificationId] = useState(initialVerificationId);
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 5;
  const [redirecting, setRedirecting] = useState(false);
  
  // Countdown timer for code expiration (15 minutes)
  const [timeRemaining, setTimeRemaining] = useState(15 * 60); // 15 minutes in seconds

  // Redirect if no email provided
  useEffect(() => {
    if (!email) {
      navigate('/login');
    }
  }, [email, navigate]);

  // Countdown timer for expiration
  useEffect(() => {
    if (timeRemaining > 0 && !success) {
      const timer = setInterval(() => {
        setTimeRemaining((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(timer);
    } else if (timeRemaining === 0) {
      setError('Verification code has expired. Please request a new code.');
    }
  }, [timeRemaining, success]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setInterval(() => {
        setResendCooldown((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [resendCooldown]);

  // Format time remaining
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs?.toString()?.padStart(2, '0')}`;
  };

  // Handle code input
  const handleCodeChange = (index, value) => {
    // Only allow numbers
    if (value && !/^[0-9]$/?.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    setError('');

    // Auto-focus next input
    if (value && index < 5) {
      const nextInput = document.getElementById(`code-input-${index + 1}`);
      nextInput?.focus();
    }

    // Auto-submit when all 6 digits are entered
    if (newCode?.every((digit) => digit !== '') && index === 5) {
      handleVerify(newCode?.join(''));
    }
  };

  // Handle backspace
  const handleKeyDown = (index, e) => {
    if (e?.key === 'Backspace' && !code?.[index] && index > 0) {
      const prevInput = document.getElementById(`code-input-${index - 1}`);
      prevInput?.focus();
    }
  };

  // Handle paste
  const handlePaste = (e) => {
    e?.preventDefault();
    const pastedData = e?.clipboardData?.getData('text')?.slice(0, 6);
    
    if (/^[0-9]{6}$/?.test(pastedData)) {
      const newCode = pastedData?.split('');
      setCode(newCode);
      handleVerify(pastedData);
    }
  };

  // Verify code
  const handleVerify = async (codeString = null) => {
    const verificationCode = codeString || code?.join('');
    
    if (verificationCode?.length !== 6) {
      setError('Please enter all 6 digits');
      return;
    }

    if (!verificationId) {
      setError('Verification session expired. Please request a new code.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Pass stateUserId so verifyCode works without an active session
      await verificationService?.verifyCode(verificationId, verificationCode, stateUserId);
      setSuccess(true);
      
      // Redirect to SMS verification if phone number exists, otherwise go to login
      setRedirecting(true);
      setTimeout(() => {
        if (phoneNumber) {
          // Send SMS verification code then navigate to SMS screen
          verificationService?.sendSmsVerification(phoneNumber, stateUserId)?.then((result) => {
              navigate('/sms-verification-screen', {
                state: {
                  phoneNumber,
                  verificationId: result?.verificationId,
                  userId: stateUserId
                }
              });
            })?.catch((err) => {
              console.error('SMS verification setup failed:', err);
              // Fall back to login if SMS fails
              navigate('/login', {
                state: {
                  message: 'Email verified successfully! Please sign in to continue.'
                }
              });
            });
        } else {
          navigate('/login', {
            state: {
              message: 'Email verified successfully! Please sign in to continue.'
            }
          });
        }
      }, 2000);
    } catch (err) {
      console.error('Verification error:', err);
      setAttempts((prev) => prev + 1);
      
      if (err?.message?.includes('expired')) {
        setError('Verification code has expired. Please request a new code.');
        setTimeRemaining(0);
      } else if (err?.message?.includes('Maximum')) {
        setError('Maximum verification attempts exceeded. Please request a new code.');
      } else if (err?.message?.includes('Invalid')) {
        setError(`Invalid code. ${maxAttempts - attempts - 1} attempts remaining.`);
      } else {
        setError(err?.message || 'Verification failed. Please try again.');
      }
      
      // Clear code inputs on error
      setCode(['', '', '', '', '', '']);
      document.getElementById('code-input-0')?.focus();
    } finally {
      setLoading(false);
    }
  };

  // Resend verification code
  const handleResend = async () => {
    if (resendCooldown > 0) return;

    setLoading(true);
    setError('');

    try {
      // Pass stateUserId so resend works without an active session
      const result = await verificationService?.sendEmailVerification(email, stateUserId);
      setVerificationId(result?.verificationId);
      setResendCooldown(60); // 60 second cooldown
      setTimeRemaining(15 * 60); // Reset timer to 15 minutes
      setAttempts(0); // Reset attempts
      setCode(['', '', '', '', '', '']); // Clear code inputs
    } catch (err) {
      console.error('Resend error:', err);
      setError(err?.message || 'Failed to resend code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Success state
  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-600 via-emerald-500 to-teal-600">
        <div className="bg-white p-8 rounded-lg shadow-xl max-w-md w-full text-center">
          <div className="mb-6">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="w-10 h-10 text-green-600 animate-bounce" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Email Verified!</h2>
            <p className="text-gray-600">
              Your account has been successfully verified.
            </p>
            <p className="text-sm text-gray-500 mt-2">
              You can now sign in to your account.
            </p>
          </div>
          
          <div className="space-y-3">
            {redirecting ? (
              <>
                <div className="flex items-center justify-center space-x-2">
                  <div className="w-2 h-2 bg-green-600 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <div className="w-2 h-2 bg-green-600 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <div className="w-2 h-2 bg-green-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
                <p className="text-sm text-gray-500">Redirecting to sign in...</p>
              </>
            ) : (
              <p className="text-sm text-gray-500">Preparing your workspace...</p>
            )}
            <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
              <div className="bg-gradient-to-r from-green-600 to-teal-600 h-2 rounded-full transition-all duration-2000 ease-in-out" style={{ width: redirecting ? '100%' : '0%' }} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main verification screen
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-600 via-emerald-500 to-teal-600 p-4">
      <div className="bg-white p-8 rounded-lg shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
            <Mail className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Verify Your Email</h2>
          <p className="text-gray-600 text-sm">
            We've sent a 6-digit verification code to
          </p>
          <p className="text-gray-800 font-medium mt-1">{email}</p>
          <button
            onClick={() => navigate('/login')}
            className="text-sm text-green-600 hover:text-green-700 mt-2 inline-flex items-center"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Change email
          </button>
        </div>

        {/* Progress Indicator */}
        <div className="mb-6">
          <div className="flex items-center justify-center space-x-2 text-sm text-gray-600">
            <span className="flex items-center">
              <span className="w-6 h-6 bg-green-600 text-white rounded-full flex items-center justify-center text-xs mr-2">1</span>
              Sign Up
            </span>
            <span className="text-gray-400">→</span>
            <span className="flex items-center font-medium text-green-600">
              <span className="w-6 h-6 bg-green-600 text-white rounded-full flex items-center justify-center text-xs mr-2">2</span>
              Verify Email
            </span>
            <span className="text-gray-400">→</span>
            <span className="flex items-center text-gray-400">
              <span className="w-6 h-6 bg-gray-200 text-gray-500 rounded-full flex items-center justify-center text-xs mr-2">3</span>
              Dashboard
            </span>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start">
            <XCircle className="w-5 h-5 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {/* Code Input */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-3 text-center">
            Enter Verification Code
          </label>
          <div className="flex justify-center space-x-2" onPaste={handlePaste}>
            {code?.map((digit, index) => (
              <input
                key={index}
                id={`code-input-${index}`}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleCodeChange(index, e?.target?.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                className="w-12 h-12 text-center text-xl font-bold border-2 border-gray-300 rounded-lg focus:border-green-500 focus:ring-2 focus:ring-green-500 focus:outline-none transition-colors"
                disabled={loading || success}
              />
            ))}
          </div>
        </div>

        {/* Timer and Attempts */}
        <div className="mb-6 flex items-center justify-between text-sm">
          <div className="text-gray-600">
            <span className="font-medium">Time remaining:</span>{' '}
            <span className={timeRemaining < 60 ? 'text-red-600 font-bold' : 'text-gray-800'}>
              {formatTime(timeRemaining)}
            </span>
          </div>
          <div className="text-gray-600">
            <span className="font-medium">Attempts:</span>{' '}
            <span className={attempts >= maxAttempts - 1 ? 'text-red-600 font-bold' : 'text-gray-800'}>
              {attempts}/{maxAttempts}
            </span>
          </div>
        </div>

        {/* Verify Button */}
        <Button
          onClick={() => handleVerify()}
          loading={loading}
          fullWidth
          className="bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700 text-white font-medium mb-4"
          disabled={code?.some((digit) => digit === '') || timeRemaining === 0}
        >
          Verify Email
        </Button>

        {/* Resend Code */}
        <div className="text-center">
          <p className="text-sm text-gray-600 mb-2">Didn't receive the code?</p>
          <button
            onClick={handleResend}
            disabled={resendCooldown > 0 || loading}
            className={`text-sm font-medium ${
              resendCooldown > 0 || loading
                ? 'text-gray-400 cursor-not-allowed' :'text-green-600 hover:text-green-700'
            }`}
          >
            {resendCooldown > 0
              ? `Resend code in ${resendCooldown}s`
              : 'Resend verification code'}
          </button>
        </div>

        {/* Help Text */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          <p className="text-xs text-gray-500 text-center">
            Check your spam folder if you don't see the email. For help, contact{' '}
            <a href="mailto:support@growguardian.com" className="text-green-600 hover:text-green-700">
              support@growguardian.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationScreen;